import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import SubmissionsChart from '../form-analytics/components/SubmissionsChart';
import DeviceBreakdown from '../form-analytics/components/DeviceBreakdown';
import FunnelAnalysis from '../form-analytics/components/FunnelAnalysis';
import GeographicDistribution from '../form-analytics/components/GeographicDistribution';
import FormActivityFeed from '../form-analytics/components/ActivityFeed';
import SubmissionsTable from '../form-analytics/components/SubmissionsTable';
import FeatureGate from '../../components/plan/FeatureGate';
import { EventAnalyticsContent } from '../event-analytics';
import { ActivityHubContent } from '../activity-hub';
import { useAuth } from '../../contexts/AuthContext';
import { getTenantUserId } from '../../services/formsService';
import { supabase } from '../../lib/supabase';
import { submissionsService } from '../../services/submissionsService';

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'BarChart3' },
  { id: 'forms', label: 'By form', icon: 'FileBarChart' },
  { id: 'activity', label: 'Activity', icon: 'Activity' },
];

const SCREENSHOT_DEMO_ENABLED = true;

const previewDeviceData = [
  { device: 'desktop', value: 1542, percentage: 54.2 },
  { device: 'mobile', value: 968, percentage: 34.0 },
  { device: 'tablet', value: 337, percentage: 11.8 },
];

const previewFunnelSteps = [
  { id: 1, name: 'Form Started', value: 4168, percentage: 100 },
  { id: 2, name: 'Personal Info', value: 3542, percentage: 85 },
  { id: 3, name: 'Contact Details', value: 3124, percentage: 75 },
  { id: 4, name: 'Message / Inquiry', value: 2847, percentage: 68 },
];

const previewLocationData = [
  { country: 'United States', flag: '🇺🇸', submissions: 1245, percentage: 43.7 },
  { country: 'United Kingdom', flag: '🇬🇧', submissions: 568, percentage: 20.0 },
  { country: 'Canada', flag: '🇨🇦', submissions: 342, percentage: 12.0 },
  { country: 'Australia', flag: '🇦🇺', submissions: 285, percentage: 10.0 },
  { country: 'Germany', flag: '🇩🇪', submissions: 198, percentage: 7.0 },
  { country: 'Others', flag: '🌍', submissions: 209, percentage: 7.3 },
];

const screenshotDemoForms = [
  { id: 'demo-leads', title: 'Lead Generation Form', status: 'published' },
  { id: 'demo-contact', title: 'Contact & Support', status: 'published' },
  { id: 'demo-quote', title: 'Project Quote Request', status: 'published' },
  { id: 'demo-event', title: 'Event Registration', status: 'published' },
  { id: 'demo-feedback', title: 'Customer Feedback', status: 'published' },
  { id: 'demo-newsletter', title: 'Newsletter Signup', status: 'draft' },
];

const buildScreenshotDemoSubmissions = () => {
  const dailyVolumes = [296, 342, 381, 407, 435, 469, 517];
  const formRotation = [
    'demo-leads',
    'demo-leads',
    'demo-leads',
    'demo-leads',
    'demo-contact',
    'demo-contact',
    'demo-quote',
    'demo-event',
    'demo-feedback',
    'demo-newsletter',
  ];
  let submissionIndex = 0;

  return dailyVolumes.flatMap((volume, dayIndex) => Array.from({ length: volume }, (_, itemIndex) => {
    const createdAt = new Date();
    createdAt.setHours(8 + (itemIndex % 12), (itemIndex * 7) % 60, 0, 0);
    createdAt.setDate(createdAt.getDate() - (6 - dayIndex));
    const index = submissionIndex++;

    return {
      id: `screenshot-demo-${index}`,
      form_id: formRotation[index % formRotation.length],
      status: index % 9 === 0 ? 'started' : 'completed',
      created_at: createdAt.toISOString(),
      submission_data: {},
    };
  }));
};

const getSubmissionFormId = (submission) => submission?.formId || submission?.form_id;
const getSubmissionCreatedAt = (submission) => submission?.createdAt || submission?.created_at;
const isCompleted = (submission) => ['completed', 'complete', 'submitted'].includes(String(submission?.status || '').toLowerCase());

const formatDateTime = (value) => {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const buildTrend = (submissions) => {
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (6 - index));
    return date;
  });

  return days.map((day) => {
    const nextDay = new Date(day);
    nextDay.setDate(nextDay.getDate() + 1);
    const daySubmissions = submissions.filter((submission) => {
      const submittedAt = new Date(getSubmissionCreatedAt(submission));
      return submittedAt >= day && submittedAt < nextDay;
    });

    return {
      date: day.toLocaleDateString([], { weekday: 'short' }),
      submissions: daySubmissions.length,
      completed: daySubmissions.filter(isCompleted).length,
    };
  });
};

const MetricCard = ({ icon, label, value, detail }) => (
  <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-heading font-semibold text-foreground">{value}</p>
        {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon name={icon} size={20} />
      </div>
    </div>
  </div>
);

const EmptyState = ({ title, detail, icon = 'BarChart3' }) => (
  <div className="rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
      <Icon name={icon} size={20} className="text-muted-foreground" />
    </div>
    <h2 className="font-semibold text-foreground">{title}</h2>
    <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{detail}</p>
  </div>
);

const Analytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'overview';
  const selectedFormId = activeTab === 'forms' ? (searchParams.get('id') || '') : '';
  const isScreenshotDemo = SCREENSHOT_DEMO_ENABLED && activeTab === 'overview';
  const displayedForms = isScreenshotDemo ? screenshotDemoForms : forms;
  const displayedSubmissions = useMemo(
    () => (isScreenshotDemo ? buildScreenshotDemoSubmissions() : submissions),
    [isScreenshotDemo, submissions],
  );

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const tenantUserId = await getTenantUserId();
      const [formsResult, submissionsResult] = await Promise.all([
        supabase.from('forms').select('id, title, status').eq('user_id', tenantUserId).order('updated_at', { ascending: false }),
        supabase
          .from('form_submissions')
          .select('id, form_id, status, created_at, submission_data, forms!inner(user_id)')
          .eq('forms.user_id', tenantUserId)
          .order('created_at', { ascending: false }),
      ]);

      if (formsResult.error) throw formsResult.error;
      if (submissionsResult.error) throw submissionsResult.error;

      setForms(formsResult.data || []);
      setSubmissions(submissionsResult.data || []);
    } catch (loadError) {
      console.error('Unable to load analytics:', loadError);
      setError('We could not load analytics right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) loadAnalytics();
  }, [authLoading, loadAnalytics, user]);

  useEffect(() => {
    if (!selectedFormId) return undefined;
    return submissionsService.subscribeToSubmissions(selectedFormId, () => loadAnalytics());
  }, [loadAnalytics, selectedFormId]);

  const formLookup = useMemo(
    () => new Map(displayedForms.map((form) => [form.id, form])),
    [displayedForms],
  );

  const selectedForm = selectedFormId ? formLookup.get(selectedFormId) : null;
  const scopedSubmissions = selectedFormId
    ? displayedSubmissions.filter((submission) => getSubmissionFormId(submission) === selectedFormId)
    : displayedSubmissions;
  const completedCount = scopedSubmissions.filter(isCompleted).length;
  const completionRate = scopedSubmissions.length
    ? `${Math.round((completedCount / scopedSubmissions.length) * 100)}%`
    : '—';
  const trend = useMemo(() => buildTrend(scopedSubmissions), [scopedSubmissions]);

  const formPerformance = useMemo(
    () => displayedForms
      .map((form) => {
        const formSubmissions = displayedSubmissions.filter((submission) => getSubmissionFormId(submission) === form.id);
        return {
          id: form.id,
          title: form.title || 'Untitled form',
          submissions: formSubmissions.length,
          completed: formSubmissions.filter(isCompleted).length,
          status: form.status || 'draft',
        };
      })
      .sort((a, b) => b.submissions - a.submissions),
    [displayedForms, displayedSubmissions],
  );

  const detailedSubmissions = useMemo(
    () => scopedSubmissions.map((submission) => {
      const responseData = submission.submission_data || submission.submissionData || {};
      return {
        ...submission,
        createdAt: getSubmissionCreatedAt(submission),
        email: submission.email || responseData.email || responseData.Email || '',
        source: submission.source || responseData.source || responseData.Source || 'Direct',
        device: submission.device || responseData.device || responseData.Device || 'unknown',
        completionTime: submission.completionTime || responseData.completionTime || 0,
        timestamp: getSubmissionCreatedAt(submission),
      };
    }),
    [scopedSubmissions],
  );

  const formActivityData = useMemo(
    () => detailedSubmissions.slice(0, 6).map((submission) => ({
      id: submission.id,
      type: isCompleted(submission) ? 'submission' : 'started',
      message: `Response received${submission.email ? ` from ${submission.email}` : ''}`,
      timestamp: getSubmissionCreatedAt(submission),
      device: submission.device,
      location: 'Not tracked',
    })),
    [detailedSubmissions],
  );

  const updateSearch = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard icon="FileText" label="Forms" value={displayedForms.length} detail={`${displayedForms.filter((form) => form.status === 'published').length} published`} />
        <MetricCard icon="Inbox" label="Responses" value={displayedSubmissions.length} detail="Across all forms" />
        <MetricCard icon="CheckCircle2" label="Completed responses" value={completedCount} detail={`${completionRate === '—' ? 'No responses yet' : `${completionRate} completion rate`}`} />
        <MetricCard icon="Globe2" label="Published forms" value={displayedForms.filter((form) => form.status === 'published').length} detail="Currently accepting responses" />
      </div>

      {displayedForms.length === 0 ? (
        <EmptyState
          title="Create a form to start tracking performance"
          detail="Once a form receives responses, its key metrics and activity will appear here."
          icon="FilePlus2"
        />
      ) : (
        <>
          <SubmissionsChart data={trend} title="Responses in the last 7 days" />
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
              <div>
                <h2 className="font-semibold text-foreground">Top forms</h2>
                <p className="text-sm text-muted-foreground">Response volume across your forms</p>
              </div>
              <button
                onClick={() => updateSearch({ tab: 'forms', id: '' })}
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                View by form
              </button>
            </div>
            <div className="divide-y divide-border">
              {formPerformance.slice(0, 5).map((form) => (
                <button
                  key={form.id}
                  onClick={() => updateSearch({ tab: 'forms', id: form.id })}
                  className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{form.title}</p>
                    <p className="text-xs text-muted-foreground capitalize">{form.status}</p>
                  </div>
                  <div className="flex items-center gap-5 text-sm">
                    <span className="text-muted-foreground"><strong className="text-foreground">{form.submissions}</strong> responses</span>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <section className="space-y-3">
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
          <strong>Conversion &amp; performance preview.</strong>{' '}
          {isScreenshotDemo
            ? 'Screenshot demo data is active for the account totals above. The detailed event models below also use sample event data.'
            : 'The account totals above are live. The detailed event models below are retained from the previous Event Analytics workspace and use sample event data until expanded event tracking is available.'}
        </div>
        <EventAnalyticsContent />
      </section>
    </div>
  );

  const renderForms = () => (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4 sm:flex sm:items-end sm:justify-between gap-4">
        <div>
          <label htmlFor="analytics-form" className="block text-sm font-medium text-foreground">Form</label>
          <p className="mt-1 text-sm text-muted-foreground">Choose a form to narrow the metrics below.</p>
        </div>
        <select
          id="analytics-form"
          value={selectedFormId}
          onChange={(event) => updateSearch({ tab: 'forms', id: event.target.value })}
          className="mt-3 sm:mt-0 min-w-[230px] rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">All forms</option>
          {forms.map((form) => (
            <option key={form.id} value={form.id}>{form.title || 'Untitled form'}</option>
          ))}
        </select>
      </div>

      {!selectedFormId && forms.length === 0 ? (
        <EmptyState title="No forms available yet" detail="Create and publish a form to see its performance here." icon="FilePlus2" />
      ) : selectedFormId && !selectedForm ? (
        <EmptyState title="That form is no longer available" detail="Choose another form from the selector to continue." icon="FileQuestion" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard icon="Inbox" label="Responses" value={scopedSubmissions.length} detail={selectedForm ? selectedForm.title : 'Across all forms'} />
            <MetricCard icon="CheckCircle2" label="Completed" value={completedCount} detail={completionRate === '—' ? 'No responses yet' : `${completionRate} completion rate`} />
            <MetricCard icon="Clock3" label="Latest response" value={scopedSubmissions.length ? formatDateTime(scopedSubmissions[0] && getSubmissionCreatedAt(scopedSubmissions[0])) : '—'} detail="Most recently received" />
            <MetricCard icon="FileText" label="Status" value={selectedForm ? (selectedForm.status || 'draft') : `${forms.length} forms`} detail={selectedForm ? 'Current form state' : 'Included in this view'} />
          </div>
          <SubmissionsChart data={trend} title={selectedForm ? `${selectedForm.title} responses` : 'All form responses'} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-lg border border-amber-300/60 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
              <strong>Advanced insight preview.</strong> Device, location, and funnel panels preserve the previous form-analysis experience, but are sample data until the separate analytics-data work is completed.
            </div>
            <FormActivityFeed activities={formActivityData} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DeviceBreakdown data={previewDeviceData} />
            <FeatureGate feature="advanced_analytics">
              <GeographicDistribution locations={previewLocationData} />
            </FeatureGate>
          </div>
          <FeatureGate feature="advanced_analytics" silent>
            <FunnelAnalysis steps={previewFunnelSteps} />
          </FeatureGate>
          <SubmissionsTable submissions={detailedSubmissions} />
        </>
      )}
    </div>
  );

  const renderActivity = () => <ActivityHubContent />;

  if (authLoading || loading) {
    return (
      <NavigationBar>
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            Loading analytics…
          </div>
        </div>
      </NavigationBar>
    );
  }

  return (
    <NavigationBar>
      <main className="min-h-[calc(100vh-64px)] bg-background">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon name="BarChart3" size={19} />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Analytics</h1>
                <p className="text-sm text-muted-foreground">Understand form performance, response trends, and account activity.</p>
              </div>
            </div>
          </div>

          <div className="mb-6 flex gap-1 overflow-x-auto rounded-lg border border-border bg-card p-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => updateSearch(tab.id === 'forms' ? { tab: tab.id } : { tab: tab.id, id: '' })}
                className={`flex min-w-max items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {error ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-5 py-4 text-sm text-destructive">
              <span>{error}</span>
              <button onClick={loadAnalytics} className="rounded-md border border-destructive/30 px-3 py-1.5 font-medium hover:bg-destructive/10">
                Try again
              </button>
            </div>
          ) : activeTab === 'overview' ? renderOverview() : activeTab === 'forms' ? renderForms() : renderActivity()}
        </div>
      </main>
    </NavigationBar>
  );
};

export default Analytics;